import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { mediaService } from '../../services/mediaService';
import { FaUpload, FaTrash, FaImage, FaVideo, FaPlus, FaPlay } from 'react-icons/fa';
import './MediaUpload.css';

const MediaUpload = ({ images, videos, festivalId, onMediaUpdate }) => {
  const { t } = useTranslation();
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach(file => formData.append('images', file));

    setUploading(true);
    try {
      if (festivalId) {
        const response = await mediaService.addMediaToFestival(festivalId, formData);
        onMediaUpdate(response.data.images, response.data.videos);
      } else {
        const response = await mediaService.uploadMedia(formData);
        onMediaUpdate([...(images || []), ...response.data.images], videos || []);
      }
    } catch (error) {
      console.error('Error uploading images:', error);
      alert('Failed to upload images');
    } finally {
      setUploading(false);
    }
  };

  const handleVideoUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const formData = new FormData();
    files.forEach(file => formData.append('videos', file));

    setUploading(true);
    try {
      if (festivalId) {
        const response = await mediaService.addMediaToFestival(festivalId, formData);
        onMediaUpdate(response.data.images, response.data.videos);
      } else {
        const response = await mediaService.uploadMedia(formData);
        onMediaUpdate(images || [], [...(videos || []), ...response.data.videos]);
      }
    } catch (error) {
      console.error('Error uploading videos:', error);
      alert('Failed to upload videos');
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteImage = async (imagePath) => {
    if (!window.confirm('Delete this image?')) return;

    try {
      // Extract type and filename from Cloudinary URL
      // URL format: https://res.cloudinary.com/dzqmtrmmf/image/upload/v1764435270/indel-calendar/images/images-1764435268187-542556561.jpg
      let type, filename;
      
      if (imagePath.includes('cloudinary.com')) {
        // For Cloudinary URLs, extract from the path
        const urlParts = imagePath.split('/');
        const typeIndex = urlParts.findIndex(part => part === 'images' || part === 'videos');
        if (typeIndex !== -1 && urlParts[typeIndex + 1]) {
          type = urlParts[typeIndex]; // 'images' or 'videos'
          filename = urlParts[typeIndex + 1]; // actual filename
        } else {
          throw new Error('Could not extract type and filename from Cloudinary URL');
        }
      } else {
        // For local URLs, use the old method
        const filename = imagePath.split('/').pop();
        type = 'images';
      }

      if (festivalId && type && filename) {
        await mediaService.removeMediaFromFestival(festivalId, type, filename);
        onMediaUpdate(images.filter(img => img !== imagePath), videos);
      } else {
        throw new Error('Missing festivalId, type, or filename');
      }
    } catch (error) {
      console.error('Error deleting image:', error);
      alert(`Failed to delete image: ${error.message}`);
    }
  };

  const handleDeleteVideo = async (videoPath) => {
    if (!window.confirm('Delete this video?')) return;

    try {
      // Extract type and filename from Cloudinary URL
      let type, filename;
      
      if (videoPath.includes('cloudinary.com')) {
        // For Cloudinary URLs, extract from the path
        const urlParts = videoPath.split('/');
        const typeIndex = urlParts.findIndex(part => part === 'images' || part === 'videos');
        if (typeIndex !== -1 && urlParts[typeIndex + 1]) {
          type = urlParts[typeIndex]; // 'images' or 'videos'
          filename = urlParts[typeIndex + 1]; // actual filename
        } else {
          throw new Error('Could not extract type and filename from Cloudinary URL');
        }
      } else {
        // For local URLs, use the old method
        const filename = videoPath.split('/').pop();
        type = 'videos';
      }

      if (festivalId && type && filename) {
        await mediaService.removeMediaFromFestival(festivalId, type, filename);
        onMediaUpdate(images, videos.filter(vid => vid !== videoPath));
      } else {
        throw new Error('Missing festivalId, type, or filename');
      }
    } catch (error) {
      console.error('Error deleting video:', error);
      alert(`Failed to delete video: ${error.message}`);
    }
  };

  return (
    <div className="media-upload">
      {/* Images Section */}
      <div className="upload-section">
        <div className="section-header">
          <div className="section-title">
            <h4>Images</h4>
          </div>
          <label className="upload-btn primary">
            <FaPlus />
            <span>Add Images</span>
            <input
              type="file"
              accept="image/*"
              multiple
              onChange={handleImageUpload}
              disabled={uploading}
            />
          </label>
        </div>
        {images && images.length > 0 ? (
          <div className="media-grid">
            {images.map((image, index) => {
              // Helper function to build proper URLs
              const buildUrl = (url) => {
                if (!url) return '';
                // If URL already starts with http/https, return as-is (Cloudinary URLs)
                if (url.startsWith('http://') || url.startsWith('https://')) {
                  return url;
                }
                // Otherwise prepend baseURL for local server URLs
                return `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${url}`;
              };

              return (
                <div key={index} className="media-card image-card">
                  <div className="media-thumbnail">
                    <img
                      src={buildUrl(image)}
                      alt={`Image ${index + 1}`}
                    />
                    <div className="media-overlay">
                      <button
                        className="delete-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteImage(image);
                        }}
                        title="Delete image"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className="media-info">
                    <span className="media-type">Image</span>
                    <span className="media-index">#{index + 1}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-media-state">
            <div className="empty-icon">
              <FaImage />
            </div>
            <p>No images uploaded yet</p>
            <span>Add images to enhance your festival content</span>
          </div>
        )}
      </div>

      {/* Videos Section */}
      <div className="upload-section">
        <div className="section-header">
          <div className="section-title">
            <h4>Videos</h4>
          </div>
          <label className="upload-btn secondary">
            <FaPlus />
            <span>Add Videos</span>
            <input
              type="file"
              accept="video/*"
              multiple
              onChange={handleVideoUpload}
              disabled={uploading}
            />
          </label>
        </div>
        {videos && videos.length > 0 ? (
          <div className="media-grid">
            {videos.map((video, index) => {
              // Helper function to build proper URLs
              const buildUrl = (url) => {
                if (!url) return '';
                // If URL already starts with http/https, return as-is (Cloudinary URLs)
                if (url.startsWith('http://') || url.startsWith('https://')) {
                  return url;
                }
                // Otherwise prepend baseURL for local server URLs
                return `${process.env.REACT_APP_API_URL || 'http://localhost:5001'}${url}`;
              };

              return (
                <div key={index} className="media-card video-card">
                  <div className="media-thumbnail">
                    <video src={buildUrl(video)} />
                    <div className="video-play-overlay">
                      <FaPlay />
                    </div>
                    <div className="media-overlay">
                      <button
                        className="delete-btn"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          handleDeleteVideo(video);
                        }}
                        title="Delete video"
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </div>
                  <div className="media-info">
                    <span className="media-type">Video</span>
                    <span className="media-index">#{index + 1}</span>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          <div className="empty-media-state">
            <div className="empty-icon">
              <FaVideo />
            </div>
            <p>No videos uploaded yet</p>
            <span>Add videos to create engaging festival content</span>
          </div>
        )}
      </div>

      {uploading && (
        <div className="upload-overlay">
          <div className="upload-spinner">
            <div className="spinner"></div>
            <p>Uploading media...</p>
            <span>Please wait while your files are being processed</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default MediaUpload;
